// Enhanced version of your existing Login.jsx - keeps your styling and structure
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';
import '../styles/Auth.css';

const Login = () => {
  console.log('=== LOGIN COMPONENT MOUNTED ===');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Enhanced user destination logic - NEW ADDITION
  const determineUserDestination = async (user, redirectPath = null) => {
    try {
      console.log('Determining destination for user:', user.id, 'redirectPath:', redirectPath);
      
      // If there's a specific redirect path (from protected route), use it first
      if (redirectPath && redirectPath !== '/' && redirectPath !== '/login') {
        console.log('Using specific redirect path:', redirectPath);
        return redirectPath;
      }
      
      // Get user roles, gallery access, and preferences
      const [rolesResult, galleryAccessResult, userProfileResult] = await Promise.all([
        supabase.from('user_roles').select('role_id').eq('user_id', user.id),
        supabase.from('gallery_access').select('*').eq('user_uuid', user.id).eq('is_active', true),
        supabase.from('users').select('preferences').eq('uuid', user.id).single()
      ]);

      // Check for errors in each query
      if (rolesResult.error) {
        console.error('Error fetching user roles:', rolesResult.error);
      }
      if (galleryAccessResult.error) {
        console.error('Error fetching gallery access:', galleryAccessResult.error);
      }
      if (userProfileResult.error) {
        console.error('Error fetching user profile:', userProfileResult.error);
      }

      const roles = rolesResult.data || [];
      const galleryAccess = galleryAccessResult.data || [];
      const userProfile = userProfileResult.data;
      const preferences = userProfile?.preferences || {};

      console.log('User capabilities:', { 
        roles, 
        galleryAccess, 
        preferences, 
        userProfile,
        userId: user.id,
        rolesError: rolesResult.error,
        galleryError: galleryAccessResult.error,
        profileError: userProfileResult.error
      });

      // No specific redirect path - go to Curator Dashboard for admin site
      console.log('No specific redirect path - routing to Curator Dashboard');
      return '/curator/dashboard';

    } catch (error) {
      console.error('Error determining user destination:', error);
      return '/curator/dashboard'; // Safe fallback
    }
  };
  
  // Get redirect path from multiple sources to ensure proper return after login
  const getRedirectPath = () => {
    // 1. Check URL search params first (e.g., /login?from=/submissions/12)
    const urlParams = new URLSearchParams(location.search);
    const fromParam = urlParams.get('from');
    if (fromParam) {
      return fromParam;
    }
    
    // 2. Check location state (e.g., from navigation with state)
    if (location.state?.from?.pathname) {
      return location.state.from.pathname;
    }
    
    // 3. Check if we came from a specific page via referrer
    if (document.referrer && document.referrer.includes(window.location.origin)) {
      const referrerPath = document.referrer.replace(window.location.origin, '');
      // Only redirect to valid internal paths
      if (referrerPath && referrerPath !== '/login' && referrerPath !== '/') {
        return referrerPath;
      }
    }
    
    // 4. Default fallback - check if we're on a submission page
    const currentPath = window.location.pathname;
    if (currentPath.includes('/submissions/') && currentPath.includes('/apply')) {
      // If we're on a submission apply page, go back to that submission
      const submissionMatch = currentPath.match(/\/submissions\/(\d+)/);
      if (submissionMatch) {
        return `/submissions/${submissionMatch[1]}`;
      }
    }
    
    // 5. Final fallback - will be used by determineUserDestination
    return '/artist/submissions';
  };
  
  const from = getRedirectPath();

  // Debug logging to help understand redirects
  useEffect(() => {
    console.log('=== LOGIN REDIRECT DEBUG ===');
    console.log('Login redirect path determined:', from);
    console.log('Current location:', location);
    console.log('Location state:', location.state);
    console.log('URL search params:', new URLSearchParams(location.search));
    console.log('=== END LOGIN REDIRECT DEBUG ===');
  }, [from, location]);

  // Check if user is already logged in and redirect them
  useEffect(() => {
    console.log('=== LOGIN COMPONENT useEffect RUNNING ===');
    const checkAuth = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        console.log('Login component: Checking auth, user:', !!user);
        if (user) {
          // User is already logged in, redirect to curator dashboard
          console.log('User already logged in, redirecting to curator dashboard');
          navigate('/curator/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('Login component: Error checking auth:', error);
      }
    };
    
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    console.log('=== FORM SUBMITTED ===');
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      console.log('=== ATTEMPTING LOGIN ===');
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        setMessage('Login successful! Redirecting...');
        
        // Route all users to the curator dashboard
        console.log('Routing user to: /curator/dashboard');
        navigate('/curator/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setLoading(true);
      setError('');
      
      const redirectUrl = `${window.location.origin}/curator/dashboard`;
      console.log('Google OAuth redirecting to:', redirectUrl);
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google sign in error:', error);
      setError(error.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo-container">
            <img 
              src="https://res.cloudinary.com/dh9jfogh1/image/upload/t_fitmax-300/v1729693346/vibrant-logo_ds6pwg.png" 
              alt="VibrantCast" 
              className="auth-logo-inline"
            />
          </div>
          <h2>Welcome Back</h2>
          <p className="auth-subtitle">
            Sign in to access your VibrantCast dashboard
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="auth-error">
            <AlertCircle size={20} />
            <div>
              <div className="error-title">Sign In Failed</div>
              <p className="error-message">{error}</p>
            </div>
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="auth-success">
            <div className="success-icon"></div>
            <div>
              <div className="success-title">Success!</div>
              <p className="success-message">{message}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-container">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-container">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button
          onClick={handleGoogleSignIn}
          className="google-signin-btn"
          disabled={loading}
        >
          <svg width="20" height="20" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <Link to="/email-login" className="email-otp-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          Email Code
        </Link>

        <div className="auth-footer">
          <div className="footer-links">
            <Link to="/forgot-password" className="footer-link">
              Forgot password?
            </Link>
            <div className="signup-prompt">
              Don't have an account?{' '}
              <Link to="/signup" className="footer-link">
                Sign up
              </Link>
            </div>
          </div>
          
          <div className="legal-text">
            <p>By signing in, you agree to our</p>
            <div className="legal-links">
              <Link to="/terms" className="footer-link">Terms of Service</Link>
              <span>and</span>
              <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;